import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Button,
  Divider,
  Paper,
  Alert,
  Modal,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Switch,
  FormControlLabel,
  CircularProgress,
  Breadcrumbs,
  Link
} from '@mui/material';
import { Business, Edit, Add, Delete, MoreVert } from '@mui/icons-material';
import apiService from '../../services/apiService';
import QuotaUsageCard from '../cards/QuotaUsageCard';
import CouponCodesCard from '../cards/CouponCodesCard';
import GhostUserManagementCard from '../cards/GhostUserManagementCard';
import ConversationStatsCard from '../cards/ConversationStatsCard';
import { useToast } from '../common/ToastProvider';
import '../styles/ContentComponents.css';
import { FEATURE_FLAG_NAMES } from '../../constants/featureFlags';
import { PLAN_DEFAULT_TERM_MONTHS, PLAN_TERM_OPTIONS } from '../../constants/constants';

function CompanyDetail() {
  const { showSuccess, showError } = useToast();
  const { companyId } = useParams();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [featureFlags, setFeatureFlags] = useState([]);
  const [loadingFlags, setLoadingFlags] = useState({});
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [planQuotas, setPlanQuotas] = useState(null);
  const [planQuotasLoading, setPlanQuotasLoading] = useState(false);
  const [termMonths, setTermMonths] = useState(null);
  const [addFlagModalOpen, setAddFlagModalOpen] = useState(false);
  const [addFlagForm, setAddFlagForm] = useState({
    featureName: '',
    featureValue: false
  });
  const [addFlagLoading, setAddFlagLoading] = useState(false);
  const [deleteFlagModalOpen, setDeleteFlagModalOpen] = useState(false);
  const [flagToDelete, setFlagToDelete] = useState(null);
  const [deleteFlagLoading, setDeleteFlagLoading] = useState(false);

  const [editForm, setEditForm] = useState({
    subscriptionPlanId: '',
    startDate: '',
    endDate: ''
  });
  const [editLoading, setEditLoading] = useState(false);
  const [isTestModalOpen, setIsTestModalOpen] = useState(false);
  const [isTestLoading, setIsTestLoading] = useState(false);
  const [isTestNewValue, setIsTestNewValue] = useState(null);
  const [companyOptionsModalOpen, setCompanyOptionsModalOpen] = useState(false);
  const [deleteCompanyModalOpen, setDeleteCompanyModalOpen] = useState(false);
  const [deleteCompanyLoading, setDeleteCompanyLoading] = useState(false);
  const [deleteCompanyConfirmation, setDeleteCompanyConfirmation] = useState('');
  const [featureFlagConfirmModalOpen, setFeatureFlagConfirmModalOpen] = useState(false);
  const [featureFlagToToggle, setFeatureFlagToToggle] = useState(null);
  const [featureFlagToggleLoading, setFeatureFlagToggleLoading] = useState(false);

  useEffect(() => {
    fetchCompanyDetail();
    fetchSubscriptionPlans();
    fetchFeatureFlags();
  }, [companyId]);

  const fetchCompanyDetail = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await apiService.fetchCompanyById(companyId);
      
      if (result.success) {
        setCompany(result.data.company);
        setOrganizations(result.data.organizations || []);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Failed to load company details');
    }
    
    setLoading(false);
  };

  const fetchSubscriptionPlans = async () => {
    try {
      const result = await apiService.fetchSubscriptionPlans();
      if (result.success) {
        setSubscriptionPlans(result.data);
      }
    } catch (err) {
      console.error('Failed to fetch subscription plans:', err);
    }
  };

  const fetchFeatureFlags = async () => {
    try {
      const result = await apiService.fetchCompanyFeatureFlags(companyId);
      if (result.success) {
        setFeatureFlags(result.data);
      }
    } catch (err) {
      console.error('Failed to fetch feature flags:', err);
    }
  };



  const handleEditClick = () => {
    setEditForm({
      subscriptionPlanId: company.subscription_plan_id || '',
      startDate: company.subscription_start_date ? company.subscription_start_date.split('T')[0] : '',
      endDate: company.subscription_end_date ? company.subscription_end_date.split('T')[0] : ''
    });
    setEditModalOpen(true);
  };

  // What the plan being picked actually grants. The end date silently cuts off model
  // serving once it passes, so the operator should see both before saving.
  const fetchPlanQuotas = async (planId) => {
    if (!planId) {
      setPlanQuotas(null);
      return;
    }
    setPlanQuotasLoading(true);
    const result = await apiService.fetchPlanQuotas(planId);
    setPlanQuotas(result.success ? (result.data?.quota_policies || []) : null);
    setPlanQuotasLoading(false);
  };

  // Term picker. Typing dates by hand is how a customer ends up cut off a year early,
  // so advancing someone picks a term and the date is computed from it.
  const endDateForTerm = (months) => {
    if (!months) return '';
    const base = editForm.startDate ? new Date(editForm.startDate) : new Date();
    const end = new Date(base);
    end.setMonth(end.getMonth() + months);
    return end.toISOString().split('T')[0];
  };

  const handleTermChange = (months) => {
    setTermMonths(months);
    handleFormChange('endDate', endDateForTerm(months));
  };

  // Picking a plan pre-fills its usual term — 3 months for a pilot, a year for growth —
  // which the operator can then change.
  const handlePlanChange = (planId) => {
    handleFormChange('subscriptionPlanId', planId);
    fetchPlanQuotas(planId);

    const plan = subscriptionPlans.find((p) => p.unique_id === planId);
    const defaultTerm = plan ? PLAN_DEFAULT_TERM_MONTHS[plan.name] : undefined;
    if (defaultTerm === undefined) return; // unknown plan — leave the dates alone

    const months = defaultTerm || 0;
    setTermMonths(months);
    handleFormChange('endDate', endDateForTerm(months));
  };

  const isPlanLapsed = () => {
    if (!company?.subscription_end_date) return false;
    return new Date(company.subscription_end_date) < new Date();
  };

  const handleModalClose = () => {
    setEditModalOpen(false);
  };

  const handleFormChange = (field, value) => {
    setEditForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFeatureFlagToggle = (flagId, featureName, newValue) => {
    // Set the flag to toggle and show confirmation modal
    setFeatureFlagToToggle({
      flagId,
      featureName,
      newValue,
      currentValue: !newValue
    });
    setFeatureFlagConfirmModalOpen(true);
  };

  const handleFeatureFlagConfirm = async () => {
    if (!featureFlagToToggle) return;
    
    setFeatureFlagToggleLoading(true);
    
    const { flagId, featureName, newValue } = featureFlagToToggle;

    // Log the data being sent (as requested)
    console.log('Feature Flag Toggle:', {
      feature_name: featureName,
      feature_value: newValue,
      company_id: companyId
    });

    try {
      const result = await apiService.updateCompanyFeatureFlag(flagId, newValue);
      
      if (result.success) {
        console.log('Feature flag updated successfully:', result.data);
        // Update the feature flags list with the response data
        setFeatureFlags(prev => 
          prev.map(flag => 
            flag.unique_id === flagId 
              ? { 
                  ...flag, 
                  feature_value: result.data.updated_data.feature_value 
                }
              : flag
          )
        );
        showSuccess('Feature flag updated successfully');
      } else {
        console.error('Failed to update feature flag:', result.error);
        showError(`Failed to update feature flag: ${result.error}`, 'Error');
      }
    } catch (error) {
      console.error('Error updating feature flag:', error);
      showError('Failed to update feature flag. Please try again.', 'Error');
    } finally {
      setFeatureFlagToggleLoading(false);
      setFeatureFlagConfirmModalOpen(false);
      setFeatureFlagToToggle(null);
    }
  };

  const handleFeatureFlagCancel = () => {
    setFeatureFlagConfirmModalOpen(false);
    setFeatureFlagToToggle(null);
  };

  const handleApply = async () => {
    setEditLoading(true);

    try {
      // Prepare the request data according to the API specification
      const requestData = {
        subscription_id: editForm.subscriptionPlanId,
        start_date: editForm.startDate ? new Date(editForm.startDate).toISOString() : null,
        end_date: editForm.endDate ? new Date(editForm.endDate).toISOString() : null
      };

      // Remove null values to match the API schema
      if (!requestData.start_date) {
        delete requestData.start_date;
      }
      if (!requestData.end_date) {
        delete requestData.end_date;
      }

      // Log the data being sent (as requested)
      const oldSubscription = {
        planId: company.subscription_plan_id,
        startDate: company.subscription_start_date,
        endDate: company.subscription_end_date
      };

      const newSubscription = {
        planId: editForm.subscriptionPlanId,
        startDate: editForm.startDate,
        endDate: editForm.endDate
      };

      console.log('Edit Subscription Data:', {
        companyId: companyId,
        oldSubscription,
        newSubscription,
        oldDates: {
          start: company.subscription_start_date,
          end: company.subscription_end_date
        },
        newDates: {
          start: editForm.startDate,
          end: editForm.endDate
        }
      });

      // Call the API to update the subscription
      const result = await apiService.updateCompanySubscription(companyId, requestData);
      
      if (result.success) {
        console.log('Subscription updated successfully:', result.data);
        // Refresh the company data to show the updated information
        await fetchCompanyDetail();
        setEditModalOpen(false);
        showSuccess('Subscription updated successfully!', 'Success');
      } else {
        console.error('Failed to update subscription:', result.error);
        showError(`Failed to update subscription: ${result.error}`, 'Error');
      }
    } catch (error) {
      console.error('Error updating subscription:', error);
      showError('Failed to update subscription. Please try again.', 'Error');
    } finally {
      setEditLoading(false);
    }
  };

  const getSubscriptionStatus = (company) => {
    if(!company.subscription_start_date) return 'No subscription';
    // No end date means the plan does not expire — that is true of the legacy free plan
    // and of any negotiated plan we have not dated, so don't call it "Free".
    if (!company.subscription_end_date && company.subscription_start_date) return 'No expiry';
    
    const endDate = new Date(company.subscription_end_date);
    const now = new Date();
    
    if (endDate < now) return 'Expired';
    if (endDate.getTime() - now.getTime() < 7 * 24 * 60 * 60 * 1000) return 'Expiring Soon';
    return 'Active';
  };

  const getTrialStatus = (company) => {
    if (!company.trial_expiring_date) return 'No trial';
    
    const trialEnd = new Date(company.trial_expiring_date);
    const now = new Date();
    
    if (trialEnd < now) return 'Expired';
    if (trialEnd.getTime() - now.getTime() < 7 * 24 * 60 * 60 * 1000) return 'Expiring Soon';
    return 'Active';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active':
        return 'success';
      case 'Expiring Soon':
        return 'warning';
      case 'Expired':
        return 'error';
      case 'No expiry':
        return 'info';
      default:
        return 'default';
    }
  };

  const getSetupStatus = (needSetup) => {
    return needSetup ? 'Setup Required' : 'Setup Complete';
  };

  const getDemoStatus = (demo) => {
    return demo ? 'Demo' : 'Production';
  };

  const formatToken = (token) => {
    if (!token) return 'No token';
    return token;
  };

  const getIndustryDisplay = (industry) => {
    if (!industry) return 'Not specified';
    return industry.charAt(0).toUpperCase() + industry.slice(1);
  };

  const getCurrentSubscriptionPlan = () => {
    if (!company?.subscription_plan_id || !subscriptionPlans.length) return null;
    return subscriptionPlans.find(plan => plan.unique_id === company.subscription_plan_id);
  };

  const getSubscriptionDisplayName = () => {
    const plan = getCurrentSubscriptionPlan();
    if (!plan) return company?.subscription_plan_id || 'No plan';
    return `${plan.name} - $${plan.price}`;
  };

  const handleAddFlagClick = () => {
    setAddFlagForm({
      featureName: '',
      featureValue: false
    });
    setAddFlagModalOpen(true);
  };

  const handleAddFlagModalClose = () => {
    setAddFlagModalOpen(false);
  };

  const handleAddFlagFormChange = (field, value) => {
    setAddFlagForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddFlagSubmit = async () => {
    if (!addFlagForm.featureName.trim()) {
      showError('Please enter a feature name', 'Validation Error');
      return;
    }

    setAddFlagLoading(true);

    try {
      const result = await apiService.createCompanyFeatureFlag(companyId, {
        feature_name: addFlagForm.featureName.trim(),
        feature_value: addFlagForm.featureValue
      });

      if (result.success) {
        console.log('Feature flag created successfully:', result.data);
        // Add the new feature flag to the list
        setFeatureFlags(prev => [...prev, result.data.created_data]);
        setAddFlagModalOpen(false);
        setAddFlagForm({ featureName: '', featureValue: false });
      } else {
        console.error('Failed to create feature flag:', result.error);
        showError(`Failed to create feature flag: ${result.error}`, 'Error');
      }
    } catch (error) {
      console.error('Error creating feature flag:', error);
      showError('Failed to create feature flag. Please try again.', 'Error');
    } finally {
      setAddFlagLoading(false);
    }
  };

  const handleDeleteFlagClick = (flag) => {
    setFlagToDelete(flag);
    setDeleteFlagModalOpen(true);
  };

  const handleDeleteFlagModalClose = () => {
    setDeleteFlagModalOpen(false);
    setFlagToDelete(null);
  };

  const handleDeleteFlagConfirm = async () => {
    if (!flagToDelete) return;

    setDeleteFlagLoading(true);

    try {
      const result = await apiService.deleteCompanyFeatureFlag(flagToDelete.unique_id);

      if (result.success) {
        console.log('Feature flag deleted successfully:', result.data);
        // Remove the deleted feature flag from the list
        setFeatureFlags(prev => prev.filter(flag => flag.unique_id !== flagToDelete.unique_id));
        setDeleteFlagModalOpen(false);
        setFlagToDelete(null);
      } else {
        console.error('Failed to delete feature flag:', result.error);
        showError(`Failed to delete feature flag: ${result.error}`, 'Error');
      }
    } catch (error) {
      console.error('Error deleting feature flag:', error);
      showError('Failed to delete feature flag. Please try again.', 'Error');
    } finally {
      setDeleteFlagLoading(false);
    }
  };

  const handleIsTestClick = () => {
    setIsTestNewValue(!company.is_test);
    setIsTestModalOpen(true);
  };

  const handleIsTestModalClose = () => {
    setIsTestModalOpen(false);
    setIsTestNewValue(null);
  };

  const handleIsTestConfirm = async () => {
    if (isTestNewValue === null) return;

    setIsTestLoading(true);
    setIsTestModalOpen(false); // Close modal immediately

    try {
      const result = await apiService.updateCompanyIsTest(company.unique_id, isTestNewValue);

      if (result.success) {
        console.log('Company is_test flag updated successfully:', result.data);
        // Update the company data with the new is_test value
        setCompany(prev => ({
          ...prev,
          is_test: isTestNewValue
        }));
        setIsTestNewValue(null);
        showSuccess(`Company is_test flag updated to ${isTestNewValue}`, 'Success');
      } else {
        console.error('Failed to update company is_test flag:', result.error);
        showError(`Failed to update company is_test flag: ${result.error}`, 'Error');
      }
    } catch (error) {
      console.error('Error updating company is_test flag:', error);
      showError('Failed to update company is_test flag. Please try again.', 'Error');
    } finally {
      setIsTestLoading(false);
    }
  };

  const handleCompanyOptionsClick = () => {
    setCompanyOptionsModalOpen(true);
  };

  const handleCompanyOptionsModalClose = () => {
    setCompanyOptionsModalOpen(false);
  };

  const handleDeleteCompanyClick = () => {
    setCompanyOptionsModalOpen(false);
    setDeleteCompanyConfirmation('');
    setDeleteCompanyModalOpen(true);
  };

  const handleDeleteCompanyModalClose = () => {
    setDeleteCompanyModalOpen(false);
    setDeleteCompanyConfirmation('');
  };

  const handleDeleteCompanyConfirm = async () => {
    if (deleteCompanyConfirmation !== company.name) {
      showError('Please enter the exact company name to confirm deletion', 'Error');
      return;
    }

    setDeleteCompanyLoading(true);

    try {
      const result = await apiService.deleteCompany(companyId);
      
      if (result.success) {
        console.log('Company deleted successfully:', result.data);
        showSuccess(`Company '${company.name}' and all related data deleted successfully`, 'Success');
        // Navigate back to companies list
        navigate('/companies');
      } else {
        console.error('Failed to delete company:', result.error);
        showError(`Failed to delete company: ${result.error}`, 'Error');
      }
    } catch (error) {
      console.error('Error deleting company:', error);
      showError('Failed to delete company. Please try again.', 'Error');
    } finally {
      setDeleteCompanyLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ width: '100vw', minHeight: '400px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ width: '100vw', minHeight: '400px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', px: 2 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      </Box>
    );
  }

  if (!company) {
    return (
      <Box sx={{ width: '100vw', minHeight: '400px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', px: 2 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>Company not found</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100vw', minHeight: '100vh', bgcolor: 'background.default', py: 4 }}>
      <Box sx={{ maxWidth: 1100, mx: 'auto', px: { xs: 1, sm: 2 }, width: '100%' }}>
        {/* Breadcrumb Navigation */}
        <Breadcrumbs sx={{ mb: 3, color: 'text.secondary' }}>
          <Link 
            href="/companies" 
            color="inherit" 
            underline="hover"
            sx={{ 
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 0.5
            }}
            onClick={(e) => {
              e.preventDefault();
              navigate('/companies');
            }}
          >
            <Business sx={{ fontSize: 16 }} />
            Companies
          </Link>
          <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Business sx={{ fontSize: 16 }} />
            {company?.name || 'Unnamed Company'}
          </Typography>
        </Breadcrumbs>
        
        {/* Page Header with Actions */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start', 
          mb: 4,
          flexWrap: { xs: 'wrap', md: 'nowrap' },
          gap: 2
        }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h4" fontWeight={700} gutterBottom sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              <Business />
              {company?.name || 'Unnamed Company'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1 
            }}>
              <Typography component="span" fontWeight={500}>Company ID:</Typography>
              <Typography 
                component="span" 
                sx={{ 
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                  backgroundColor: 'grey.100',
                  px: 1,
                  py: 0.25,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'grey.300'
                }}
              >
                {company?.unique_id || 'N/A'}
              </Typography>
            </Typography>
          </Box>
          
          <Box sx={{ 
            display: 'flex', 
            gap: 1, 
            flexShrink: 0,
            flexDirection: { xs: 'row', sm: 'row' }
          }}>
            <Button 
              variant="outlined" 
              startIcon={<MoreVert />}
              onClick={handleCompanyOptionsClick}
              sx={{ 
                minWidth: 'auto',
                px: 2
              }}
            >
              Options
            </Button>
          </Box>
        </Box>

        {/* A lapsed plan blocks the customer's whole app, so say it loudly rather than
            leaving it to be read off a date field. */}
        {isPlanLapsed() && (
          <Alert severity="error" sx={{ mb: 3 }} action={
            <Button color="inherit" size="small" onClick={handleEditClick}>
              Change plan
            </Button>
          }>
            <strong>This plan ended on {apiService.formatDate(company.subscription_end_date)}.</strong>{' '}
            The customer is blocked from the app until the end date is extended or the plan
            is changed.
          </Alert>
        )}

        {/* Overview & Subscription Section */}
        <Card sx={{ mb: 4, width: '100%' }}>
          <CardContent>
            <Grid container spacing={3} justifyContent="center" sx={{ maxWidth: 900, mx: 'auto' }}>
              <Grid item xs={12} md={6}>
                {/* Overview Card */}
                <Card sx={{ height: '100%', bgcolor: 'background.default', boxShadow: 0, border: '1px solid', borderColor: 'divider' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom fontWeight={600}>
                      📊 Company Overview
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box>
                        <Typography variant="body2" color="text.secondary">Company Name</Typography>
                        <Typography variant="body1" fontWeight={600}>{company.name}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">Company ID</Typography>
                        <Typography variant="body1" fontWeight={600} sx={{ wordBreak: 'break-all' }}>{company.unique_id}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">Created Date</Typography>
                        <Typography variant="body1" fontWeight={600}>{company.create_date ? apiService.formatDate(company.create_date) : 'N/A'}</Typography>
                      </Box>
                      <Divider />
                      <Box>
                        <Typography variant="body2" color="text.secondary">Subscription Plan</Typography>
                        <Typography variant="body1" fontWeight={600}>{company.subscription_plan_id || 'No plan'}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">is_test</Typography>
                        <Box sx={{ mt: 0.5, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
                          {isTestLoading ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CircularProgress size={16} />
                              <Typography variant="body2" color="text.secondary">Updating...</Typography>
                            </Box>
                          ) : (
                            <Chip 
                              label={company.is_test ? 'true' : 'false'} 
                              color={company.is_test ? 'warning' : 'success'} 
                              size="small" 
                              variant="filled"
                              onClick={handleIsTestClick}
                              sx={{ 
                                cursor: 'pointer',
                                fontWeight: 600,
                                letterSpacing: 0.5,
                                px: 1.5,
                                '&:hover': {
                                  boxShadow: 2,
                                  opacity: 0.9
                                },
                                transition: 'all 0.2s',
                              }}
                            />
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                {/* Subscription Card */}
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6">
                      💳 Subscription Information
                    </Typography>
                      <IconButton 
                        size="small" 
                        onClick={handleEditClick}
                        sx={{ color: 'primary.main' }}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    
                    <Box sx={{ flexGrow: 1 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Typography variant="body2" color="text.secondary">
                            Subscription Status
                          </Typography>
                          <Chip 
                            label={getSubscriptionStatus(company)} 
                            color={getStatusColor(getSubscriptionStatus(company))} 
                            size="medium" 
                            variant="outlined"
                            sx={{ mt: 0.5 }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="body2" color="text.secondary">
                            Subscription Plan
                          </Typography>
                          <Typography variant="body1" fontWeight="medium">
                            {getSubscriptionDisplayName()}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Start Date
                          </Typography>
                          <Typography variant="body1" fontWeight="medium">
                            {company.subscription_start_date ? apiService.formatDate(company.subscription_start_date) : 'N/A'}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            End Date
                          </Typography>
                          <Typography variant="body1" fontWeight="medium">
                            {company.subscription_end_date ? apiService.formatDate(company.subscription_end_date) : 'N/A'}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Trial & Feature Flags Section */}
        <Card sx={{ mb: 4, width: '100%' }}>
          <CardContent>
            <Grid container spacing={3} justifyContent="center" sx={{ maxWidth: 900, mx: 'auto' }}>
              <Grid item xs={12} md={6}>
                {/* Trial Card */}
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="h6" gutterBottom>
                      🎯 Trial Information
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    
                    <Box sx={{ flexGrow: 1 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Typography variant="body2" color="text.secondary">
                            Trial Status
                          </Typography>
                          <Chip 
                            label={getTrialStatus(company)} 
                            color={getStatusColor(getTrialStatus(company))} 
                            size="medium" 
                            variant="outlined"
                            sx={{ mt: 0.5 }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="body2" color="text.secondary">
                            Trial Expiring Date
                          </Typography>
                          <Typography variant="body1" fontWeight="medium">
                            {company.trial_expiring_date ? apiService.formatDate(company.trial_expiring_date) : 'N/A'}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                {/* Feature Flags Card */}
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6">
                        🚩 Feature Flags
                      </Typography>
                      <IconButton 
                        size="small" 
                        onClick={handleAddFlagClick}
                        sx={{ color: 'primary.main' }}
                      >
                        <Add fontSize="small" />
                      </IconButton>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    
                    <Box sx={{ flexGrow: 1 }}>
                      {featureFlags.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          No feature flags found for this company
                        </Typography>
                      ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {featureFlags.map((flag) => (
                            <Box key={flag.unique_id} sx={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'center',
                              p: 1,
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 1
                            }}>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body1" fontWeight="medium">
                                  {flag.feature_name}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  ID: {flag.unique_id}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={flag.feature_value}
                                      onChange={(e) => handleFeatureFlagToggle(flag.unique_id, flag.feature_name, e.target.checked)}
                                      color="primary"
                                      disabled={loadingFlags[flag.unique_id]}
                                      sx={{
                                        '& .MuiSwitch-thumb': {
                                          opacity: loadingFlags[flag.unique_id] ? 0.6 : 1,
                                        },
                                        '& .MuiSwitch-track': {
                                          opacity: loadingFlags[flag.unique_id] ? 0.6 : 1,
                                        }
                                      }}
                                    />
                                  }
                                  label=""
                                />
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteFlagClick(flag)}
                                  sx={{ 
                                    color: 'error.main',
                                    '&:hover': {
                                      backgroundColor: 'error.light',
                                      color: 'white'
                                    }
                                  }}
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Quota Usage Section */}
        <Card sx={{ mb: 4, width: '100%' }}>
          <CardContent>
            <QuotaUsageCard companyId={companyId} />
          </CardContent>
        </Card>

        {/* Conversation Statistics Section */}
        <Card sx={{ mb: 4, width: '100%' }}>
          <CardContent>
            <ConversationStatsCard companyId={companyId} />
          </CardContent>
        </Card>

        {/* Coupon Codes Section */}
        <Card sx={{ mb: 4, width: '100%' }}>
          <CardContent>
            <CouponCodesCard companyId={companyId} />
          </CardContent>
        </Card>

        {/* Ghost User Management Section */}
        <Card sx={{ mb: 4, width: '100%' }}>
          <CardContent>
            <GhostUserManagementCard companyId={companyId} />
          </CardContent>
        </Card>

        {/* Projects Section */}
        <Card sx={{ mb: 4, width: '100%' }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                🏛️ Projects ({organizations.length})
              </Typography>
              <Chip 
                label={`${organizations.length} project${organizations.length !== 1 ? 's' : ''}`}
                color="primary"
                variant="outlined"
              />
            </Box>
            <Divider sx={{ mb: 3 }} />
            
            {organizations.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  No projects found for this company
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={2}>
                {organizations.map((org) => (
                  <Grid item xs={12} md={6} lg={4} key={org.unique_id}>
                    <Paper 
                      elevation={2} 
                      sx={{ 
                        p: 2, 
                        height: '100%',
                        border: '1px solid',
                        borderColor: 'divider',
                        cursor: 'pointer',
                        '&:hover': {
                          borderColor: 'primary.main',
                          boxShadow: 3
                        }
                      }}
                      onClick={() => navigate(`/project/${org.unique_id}`)}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Typography variant="h6" sx={{ 
                          fontWeight: 600, 
                          fontSize: '1rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: '70%'
                        }}>
                          {org.organization_name || 'Unnamed Project'}
                        </Typography>
                        <Chip 
                          label={getDemoStatus(org.demo)} 
                          color={org.demo ? 'info' : 'primary'} 
                          size="small" 
                          variant="outlined"
                        />
                      </Box>
                      
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        ID: {org.unique_id}
                      </Typography>
                      
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Industry: {getIndustryDisplay(org.industry)}
                        </Typography>
                      </Box>
                      
                      {org.description && (
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            mb: 2,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                        >
                          {org.description}
                        </Typography>
                      )}
                      
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                        <Chip 
                          label={getSetupStatus(org.need_setup)} 
                          color={org.need_setup ? 'warning' : 'success'} 
                          size="small" 
                          variant="outlined"
                        />
                        {org.token && (
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              wordBreak: 'break-all',
                              fontFamily: 'monospace',
                              fontSize: '0.75rem',
                              backgroundColor: 'grey.100',
                              p: 0.5,
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: 'grey.300'
                            }}
                          >
                            Token: {formatToken(org.token)}
                          </Typography>
                        )}
                      </Box>
                      
                      <Typography variant="caption" color="text.secondary">
                        Created: {org.create_date ? apiService.formatDate(org.create_date) : 'N/A'}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            )}
          </CardContent>
        </Card>

        {/* Modals remain unchanged */}
        {/* Subscription Edit Modal */}
        <Modal
          open={editModalOpen}
          onClose={handleModalClose}
          aria-labelledby="subscription-edit-modal"
          aria-describedby="subscription-edit-form"
        >
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
          }}>
            <Typography variant="h6" gutterBottom>
              Edit Subscription
            </Typography>
            
            <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
              <FormControl fullWidth>
                <InputLabel id="subscription-plan-label">Subscription Plan</InputLabel>
                <Select
                  labelId="subscription-plan-label"
                  value={editForm.subscriptionPlanId}
                  label="Subscription Plan"
                  onChange={(e) => handlePlanChange(e.target.value)}
                  disabled={editLoading}
                >
                  <MenuItem value="">
                    <em>No plan</em>
                  </MenuItem>
                  {subscriptionPlans.map((plan) => (
                    <MenuItem key={plan.unique_id} value={plan.unique_id}>
                      {plan.name} - ${plan.price} (ID: {plan.unique_id})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* What this plan grants — so nobody moves a customer onto it blind. */}
              {editForm.subscriptionPlanId && (
                <Box sx={{ mt: -1 }}>
                  {planQuotasLoading ? (
                    <CircularProgress size={18} />
                  ) : planQuotas === null ? (
                    <Typography variant="caption" color="text.secondary">
                      Could not load this plan&apos;s allowances.
                    </Typography>
                  ) : planQuotas.length === 0 ? (
                    <Typography variant="caption" color="text.secondary">
                      No quotas on this plan — every feature is unmetered (unlimited).
                    </Typography>
                  ) : (
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {planQuotas.map((q) => (
                        <Chip
                          key={q.unique_id}
                          size="small"
                          label={`${q.feature}: ${q.feature_available_amount}`}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              )}

              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={editForm.startDate}
                onChange={(e) => handleFormChange('startDate', e.target.value)}
                disabled={editLoading}
                InputLabelProps={{
                  shrink: true,
                }}
              />

              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={editForm.endDate}
                onChange={(e) => handleFormChange('endDate', e.target.value)}
                disabled={editLoading}
                InputLabelProps={{
                  shrink: true,
                }}
                helperText="Leave empty for a plan that does not expire. Once this date passes, the customer is blocked."
              />

              <FormControl fullWidth size="small">
                <InputLabel id="term-label">Term</InputLabel>
                <Select
                  labelId="term-label"
                  value={termMonths === null ? '' : termMonths}
                  label="Term"
                  onChange={(e) => handleTermChange(e.target.value)}
                  disabled={editLoading}
                >
                  <MenuItem value="">
                    <em>Custom — use the date above</em>
                  </MenuItem>
                  {PLAN_TERM_OPTIONS.map((opt) => (
                    <MenuItem key={opt.label} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                <Button 
                  variant="outlined" 
                  onClick={handleModalClose}
                  disabled={editLoading}
                >
                  Cancel
                </Button>
                <Button 
                  variant="contained" 
                  onClick={handleApply}
                  disabled={editLoading}
                  sx={{ minWidth: 80 }}
                >
                  {editLoading ? 'Updating...' : 'Apply'}
                </Button>
              </Box>
            </Box>
          </Box>
        </Modal>

        {/* Add Feature Flag Modal */}
        <Modal
          open={addFlagModalOpen}
          onClose={handleAddFlagModalClose}
          aria-labelledby="add-feature-flag-modal"
          aria-describedby="add-feature-flag-form"
        >
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
          }}>
            <Typography variant="h6" gutterBottom>
              Add Feature Flag
            </Typography>
            
            <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
              <FormControl fullWidth disabled={addFlagLoading}>
                <InputLabel>Feature Name</InputLabel>
                <Select
                  value={addFlagForm.featureName}
                  onChange={(e) => handleAddFlagFormChange('featureName', e.target.value)}
                  label="Feature Name"
                >
                  {FEATURE_FLAG_NAMES.map((flagName) => (
                    <MenuItem key={flagName} value={flagName}>
                      {flagName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControlLabel
                control={
                  <Switch
                    checked={addFlagForm.featureValue}
                    onChange={(e) => handleAddFlagFormChange('featureValue', e.target.checked)}
                    color="primary"
                    disabled={addFlagLoading}
                  />
                }
                label="Feature Value (Enabled/Disabled)"
              />

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                <Button 
                  variant="outlined" 
                  onClick={handleAddFlagModalClose}
                  disabled={addFlagLoading}
                >
                  Cancel
                </Button>
                <Button 
                  variant="contained" 
                  onClick={handleAddFlagSubmit}
                  disabled={addFlagLoading || !addFlagForm.featureName.trim()}
                  sx={{ minWidth: 80 }}
                >
                  {addFlagLoading ? 'Adding...' : 'Add'}
                </Button>
              </Box>
            </Box>
          </Box>
        </Modal>

        {/* Delete Feature Flag Confirmation Modal */}
        <Modal
          open={deleteFlagModalOpen}
          onClose={handleDeleteFlagModalClose}
          aria-labelledby="delete-feature-flag-modal"
          aria-describedby="delete-feature-flag-confirmation"
        >
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
          }}>
            <Typography variant="h6" gutterBottom color="error">
              Delete Feature Flag
            </Typography>
            
            {flagToDelete && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Are you sure you want to delete the feature flag:
                </Typography>
                <Box sx={{ 
                  p: 2, 
                  bgcolor: 'grey.50', 
                  borderRadius: 1, 
                  border: '1px solid',
                  borderColor: 'grey.300',
                  mb: 3
                }}>
                  <Typography variant="body1" fontWeight="medium">
                    {flagToDelete.feature_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    ID: {flagToDelete.unique_id}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Current Value: {flagToDelete.feature_value ? 'Enabled' : 'Disabled'}
                  </Typography>
                </Box>
                <Typography variant="body2" color="error" sx={{ mb: 3 }}>
                  This action cannot be undone.
                </Typography>
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button 
                variant="outlined" 
                onClick={handleDeleteFlagModalClose}
                disabled={deleteFlagLoading}
              >
                Cancel
              </Button>
              <Button 
                variant="contained" 
                color="error"
                onClick={handleDeleteFlagConfirm}
                disabled={deleteFlagLoading}
                sx={{ minWidth: 80 }}
              >
                {deleteFlagLoading ? 'Deleting...' : 'Delete'}
              </Button>
            </Box>
          </Box>
        </Modal>

        {/* Is Test Modal */}
        <Modal
          open={isTestModalOpen}
          onClose={handleIsTestModalClose}
          aria-labelledby="is-test-modal"
          aria-describedby="is-test-confirmation"
        >
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
          }}>
            <Typography variant="h6" gutterBottom>
              Update is_test Flag
            </Typography>
            
            <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Are you sure you want to update the is_test flag to:
              </Typography>
              <Box sx={{ 
                p: 2, 
                bgcolor: 'grey.50', 
                borderRadius: 1, 
                border: '1px solid',
                borderColor: 'grey.300',
                mb: 3
              }}>
                <Typography variant="body1" fontWeight="medium">
                  {isTestNewValue ? 'true' : 'false'}
                </Typography>
              </Box>
              <Typography variant="body2" color="error" sx={{ mb: 3 }}>
                This action cannot be undone.
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button 
                  variant="outlined" 
                  onClick={handleIsTestModalClose}
                  disabled={isTestLoading}
                >
                  Cancel
                </Button>
                <Button 
                  variant="contained" 
                  onClick={handleIsTestConfirm}
                  disabled={isTestLoading}
                  sx={{ minWidth: 80 }}
                >
                  {isTestLoading ? 'Updating...' : 'Update'}
                </Button>
              </Box>
            </Box>
          </Box>
        </Modal>

        {/* Company Options Modal */}
        <Modal
          open={companyOptionsModalOpen}
          onClose={handleCompanyOptionsModalClose}
          aria-labelledby="company-options-modal"
          aria-describedby="company-options-menu"
        >
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 300,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 0,
            overflow: 'hidden'
          }}>
            <Box sx={{ 
              p: 2, 
              borderBottom: '1px solid',
              borderColor: 'divider',
              bgcolor: 'grey.50'
            }}>
              <Typography variant="h6">
                Company Options
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {company?.name}
              </Typography>
            </Box>
            
            <Box sx={{ p: 1 }}>
              <Button
                fullWidth
                variant="text"
                color="error"
                startIcon={<Delete />}
                onClick={handleDeleteCompanyClick}
                sx={{
                  justifyContent: 'flex-start',
                  px: 2,
                  py: 1.5,
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: 'error.light',
                    color: 'error.contrastText'
                  }
                }}
              >
                Delete Company
              </Button>
            </Box>
          </Box>
        </Modal>

        {/* Delete Company Confirmation Modal */}
        <Modal
          open={deleteCompanyModalOpen}
          onClose={handleDeleteCompanyModalClose}
          aria-labelledby="delete-company-modal"
          aria-describedby="delete-company-confirmation"
        >
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 500,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
          }}>
            <Typography variant="h6" gutterBottom color="error">
              🗑️ Delete Company
            </Typography>
            
            <Box sx={{ mt: 3 }}>
              <Alert severity="error" sx={{ mb: 3 }}>
                <Typography variant="body1" fontWeight="medium">
                  This action cannot be undone!
                </Typography>
                <Typography variant="body2">
                  Deleting this company will permanently remove all related data including:
                </Typography>
                <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                  <li>All projects for this company</li>
                  <li>All user roles and permissions</li>
                  <li>All dashboard plots and integrations</li>
                  <li>All usage records and quotas</li>
                  <li>All discount and feature code usages</li>
                  <li>The company itself</li>
                </Box>
              </Alert>

              <Box sx={{ mb: 3 }}>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  To confirm deletion, please enter the exact company name:
                </Typography>
                <Box sx={{ 
                  p: 2, 
                  bgcolor: 'grey.50', 
                  borderRadius: 1, 
                  border: '1px solid',
                  borderColor: 'grey.300',
                  mb: 2
                }}>
                  <Typography variant="body1" fontWeight="medium" color="error">
                    {company?.name}
                  </Typography>
                </Box>
                
                <TextField
                  fullWidth
                  label="Enter company name to confirm"
                  value={deleteCompanyConfirmation}
                  onChange={(e) => setDeleteCompanyConfirmation(e.target.value)}
                  placeholder={`Type "${company?.name}" to confirm`}
                  disabled={deleteCompanyLoading}
                  error={deleteCompanyConfirmation !== '' && deleteCompanyConfirmation !== company?.name}
                  helperText={
                    deleteCompanyConfirmation !== '' && deleteCompanyConfirmation !== company?.name
                      ? 'Company name does not match'
                      : ''
                  }
                  sx={{ mt: 1 }}
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button 
                  variant="outlined" 
                  onClick={handleDeleteCompanyModalClose}
                  disabled={deleteCompanyLoading}
                >
                  Cancel
                </Button>
                <Button 
                  variant="contained" 
                  color="error"
                  onClick={handleDeleteCompanyConfirm}
                  disabled={deleteCompanyLoading || deleteCompanyConfirmation !== company?.name}
                  sx={{ minWidth: 100 }}
                >
                  {deleteCompanyLoading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={16} color="inherit" />
                      Deleting...
                    </Box>
                  ) : (
                    'Delete Company'
                  )}
                </Button>
              </Box>
            </Box>
          </Box>
        </Modal>

        {/* Feature Flag Toggle Confirmation Modal */}
        <Modal
          open={featureFlagConfirmModalOpen}
          onClose={handleFeatureFlagCancel}
          aria-labelledby="feature-flag-toggle-modal"
          aria-describedby="feature-flag-toggle-confirmation"
        >
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 450,
            bgcolor: 'background.paper',
            borderRadius: 3,
            boxShadow: 24,
            p: 4,
          }}>
            <Typography variant="h6" gutterBottom color="primary" sx={{ mb: 3, fontWeight: 600 }}>
              Toggle Feature Flag
            </Typography>
            
            {featureFlagToToggle && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>
                  Are you sure you want to change the feature flag:
                </Typography>
                <Box sx={{ 
                  p: 3, 
                  bgcolor: 'primary.light', 
                  borderRadius: 2, 
                  border: '1px solid',
                  borderColor: 'primary.main',
                  mb: 3
                }}>
                  <Typography variant="h6" fontWeight="600" sx={{ mb: 1 }}>
                    {featureFlagToToggle.featureName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    ID: {featureFlagToToggle.flagId}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Current Value: {featureFlagToToggle.currentValue ? 'Enabled' : 'Disabled'}
                  </Typography>
                  <Typography variant="body2" color="primary.main" fontWeight="600">
                    New Value: {featureFlagToToggle.newValue ? 'Enabled' : 'Disabled'}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3, fontWeight: 500 }}>
                  This will immediately update the feature flag for this company.
                </Typography>
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 4 }}>
              <Button 
                variant="outlined" 
                onClick={handleFeatureFlagCancel}
                disabled={featureFlagToggleLoading}
              >
                Cancel
              </Button>
              <Button 
                variant="contained" 
                color="primary"
                onClick={handleFeatureFlagConfirm}
                disabled={featureFlagToggleLoading}
                startIcon={featureFlagToggleLoading ? <CircularProgress size={16} /> : null}
              >
                {featureFlagToggleLoading ? 'Updating...' : 'Confirm'}
              </Button>
            </Box>
          </Box>
        </Modal>
      </Box>
    </Box>
  );
}

export default CompanyDetail; 